import { CopyOutlined, LoadingOutlined, SafetyOutlined } from '@ant-design/icons';
import { Button, Modal, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import PayQRCodes from '../../../components/PayQRCodes';
import usePayOrder, { queryPaid } from '../../../components/PayQRCodes/usePayOrder';
import {
    FORMAT_PRICES,
    copyText,
    errorText,
    formatYuan,
    getFormatOrder,
    isMobileUa,
} from '../api';
import styles from './FormatPayModal.module.scss';

const qrSize = () => (typeof window !== 'undefined' && window.innerWidth < 420 ? 108 : 128);

/** H5 支付宝返回的是自动提交的表单（或跳转地址），新开页面提交 */
const openAlipay = (form) => {
    if (/^https?:\/\//i.test(form)) {
        window.location.href = form;
        return;
    }
    const win = window.open('', '_blank');
    if (!win) {
        message.info('请允许浏览器弹出新页面后重试');
        return;
    }
    win.document.open();
    win.document.write(form);
    win.document.close();
};

/**
 * 格式精修扫码付款：订单号即付款单号，支付宝 / 微信双码 + 轮询到账（与新版写作付款同一套组件）。
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {{orderNo: string, payNo?: string, price?: number, rush?: boolean, schoolName?: string, fileName?: string}} props.order
 * @param {() => void} props.onClose
 * @param {(orderNo: string) => void} props.onPaid
 */
export default function FormatPayModal({ open, order, onClose, onPaid }) {
    const [size, setSize] = useState(qrSize);
    const [checking, setChecking] = useState(false);
    const paidRef = useRef(false);
    const onPaidRef = useRef(onPaid);
    onPaidRef.current = onPaid;

    const orderNo = order?.orderNo || '';
    const payNo = order?.payNo || orderNo;

    const handlePaid = useCallback(() => {
        if (paidRef.current) return;
        paidRef.current = true;
        onPaidRef.current?.();
    }, []);

    const { channels, start, retry, reset, stop } = usePayOrder({ onPaid: handlePaid });

    // 打开即向支付宝、微信下单并开始轮询到账；关闭时停止
    useEffect(() => {
        if (!open || !payNo) {
            reset();
            return undefined;
        }
        paidRef.current = false;
        setSize(qrSize());
        start(payNo);
        return () => stop();
    }, [open, payNo, start, stop, reset]);

    const handleCheck = async () => {
        if (!payNo) return;
        setChecking(true);
        try {
            let paid = await queryPaid(payNo).catch(() => false);
            if (!paid && orderNo) {
                const status = await getFormatOrder(orderNo).catch(() => null);
                paid = !!status?.paid;
            }
            if (paid) {
                stop();
                handlePaid();
                return;
            }
            message.info('暂未查询到付款，请稍候再试；如已扣款请联系客服并提供订单号');
        } catch (error) {
            message.error(errorText(error, '查询失败，请稍后重试'));
        } finally {
            setChecking(false);
        }
    };

    const rush = !!order?.rush;
    const amount = order?.price ?? (rush ? FORMAT_PRICES.rush : FORMAT_PRICES.normal);
    const showMobilePay = isMobileUa();

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={640}
            centered
            maskClosable={false}
            destroyOnClose
            className={styles.modal}
        >
            <div className={styles.head}>
                <h2 className={styles.title}>扫码支付</h2>
                <p className={styles.subtitle}>免登录，支付宝、微信扫任一码即可，到账后自动弹出客服二维码</p>
            </div>

            <div className={styles.body}>
                <div className={styles.summary}>
                    <div className={styles.row}>
                        <span>服务</span>
                        <b>论文格式人工精修 · {rush ? '加急（24小时内交付）' : '普通（48小时内交付）'}</b>
                    </div>
                    {order?.schoolName ? (
                        <div className={styles.row}>
                            <span>学校</span>
                            <b>{order.schoolName}</b>
                        </div>
                    ) : null}
                    {order?.fileName ? (
                        <div className={styles.row}>
                            <span>原文</span>
                            <b className={styles.fileName} title={order.fileName}>
                                {order.fileName}
                            </b>
                        </div>
                    ) : null}
                    <div className={styles.total}>
                        <span>应付</span>
                        <b>¥{formatYuan(amount)}</b>
                    </div>
                </div>

                <div className={styles.payCol}>
                    <PayQRCodes
                        alipay={channels.alipay}
                        wechat={channels.wechat}
                        onRetry={retry}
                        size={size}
                    />
                    {showMobilePay ? (
                        <div className={styles.mobilePay}>
                            {channels.alipay?.form ? (
                                <Button block onClick={() => openAlipay(channels.alipay.form)}>
                                    打开支付宝付款
                                </Button>
                            ) : null}
                            {channels.wechat?.mwebUrl ? (
                                <Button block href={channels.wechat.mwebUrl}>
                                    打开微信付款
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                    <div className={styles.secure}>
                        <SafetyOutlined />
                        支付成功后请添加客服，发送订单号获取精修文件
                    </div>
                    <Button
                        type="link"
                        size="small"
                        className={styles.check}
                        disabled={checking}
                        onClick={handleCheck}
                    >
                        {checking ? <LoadingOutlined /> : null}
                        我已完成支付
                    </Button>
                </div>
            </div>

            {orderNo ? (
                <div className={styles.orderNo}>
                    <span className={styles.orderNoLabel}>订单号</span>
                    <span className={styles.orderNoValue}>{orderNo}</span>
                    <button
                        type="button"
                        className={styles.copyBtn}
                        aria-label="复制订单号"
                        onClick={() => copyText(orderNo, message)}
                    >
                        <CopyOutlined />
                    </button>
                </div>
            ) : null}
        </Modal>
    );
}
